import { Injectable } from "@angular/core";
import * as math from 'mathjs';
import * as moment from "moment";
import { BehaviorSubject } from "rxjs";
import { Filter, FilterCondition, FilterConditionOperation } from "src/app/classes";
import { Term } from "../../edit/edit-userfilter/edit-userfilter.component";

@Injectable()
export class FilterConditionsFormComponentService {

    lastInterpretation: string = null;

    private _statementValid = new BehaviorSubject(false);
    statementValid$ = this._statementValid.asObservable();

    private _corruptedConstantInStatement = new BehaviorSubject(false);
    corruptedConstantInStatement$ = this._corruptedConstantInStatement.asObservable();

    filter = null;
    compiledStatement;
    compiledFilters;

    compiled: number = moment().unix();

    analyzeStatement(serializedStatement: string): Filter {
        if (serializedStatement) {
            let interpreted = null;
            try { interpreted = math.parse(serializedStatement) } catch (e) {
                this._statementValid.next(false);
            } finally {
                if (interpreted) {
                    this.lastInterpretation = interpreted;
                    var lastFilter: Filter = { isNew: true } as Filter;
                    lastFilter.filtercondition = [];
                    let filters: Filter[] = [lastFilter];
                    let stack = [];
                    var node = { parent: lastFilter, node: interpreted };
                    var breakWhileLoop = false;
                    while (node && !breakWhileLoop) {
                        switch (Object.getPrototypeOf(node.node).type) {
                            case 'OperatorNode':
                                var type: string;
                                switch (node.node.op) {
                                    case "&": type = "AND"; break;
                                    case "|": type = "OR"; break;
                                    default:
                                        console.warn("unknown filter operation: " + node.node.op);
                                        breakWhileLoop = true;
                                        continue;
                                }
                                if (node.parent.type == null || typeof (node.parent.type) == "undefined") {
                                    node.parent.type = type;
                                    node.parent.mathJSNode = node.node;
                                } else if (node.parent.type != type) {
                                    lastFilter = { filteR1: [], mathJSNode: node.node, isNew: true } as Filter;
                                    if (!Array.isArray(node.parent.filteR1)) node.parent.filteR1 = [lastFilter];
                                    else node.parent.filteR1.push(lastFilter);
                                    lastFilter.filtercondition = [];
                                    filters.push(lastFilter);
                                }
                                lastFilter.type = type;
                                for (let arg of node.node.args) stack.push({ parent: lastFilter, node: arg });
                                break
                            case 'ConstantNode':
                                let array = node.node.value.split("=");
                                if (array.length != 2) {
                                    this._corruptedConstantInStatement.next(true);
                                    breakWhileLoop = true;
                                } else this._corruptedConstantInStatement.next(false);
                                var isNot = false, operation = FilterConditionOperation.EQUAL, att = array[0];
                                let lastChar = att[att.length - 1];
                                if (lastChar === '!') {
                                    att = att.slice(0, -1);
                                    isNot = true;
                                    operation = FilterConditionOperation.UNEQUAL;
                                } else if (lastChar === '%') {
                                    if (att[att.length - 2] === '!') {
                                        operation = FilterConditionOperation.INCLUDESNOT;
                                        att = att.slice(0, -2);
                                    } else {
                                        operation = FilterConditionOperation.INCLUDES;
                                        att = att.slice(0, -1);
                                    }
                                }
                                var statement = array[1];
                                if (typeof statement === 'string') {
                                    statement = statement.trim();
                                }
                                node.parent.filtercondition.push(
                                    { attribute: att, value: statement, operation: operation, isnot: isNot } as any
                                );
                                break
                            case 'SymbolNode':
                                console.warn("invalid input ...");
                                break
                            case 'ParenthesisNode':
                                stack.push({ parent: lastFilter, node: node.node.content });
                                break;
                        }
                        if (stack.length > 0) {
                            node = stack[0];
                            stack.splice(0, 1);
                        } else node = null;
                    }
                    if (breakWhileLoop) this._statementValid.next(false);
                    else {
                        this._statementValid.next(true);
                        this.compiledFilters = filters;

                        let rootFilter = this._getRootFilter(filters);
                        if (rootFilter) {
                            this.filter = rootFilter;
                            this.compiled = moment().unix();
                            this._updateRepresentation(filters);
                            return rootFilter;
                        }
                    }

                    if (!this._statementValid) {
                        this.compiledStatement = null;
                        return;
                    }

                    this._updateRepresentation(filters);
                }
            }
        } else this.compiledStatement = null;
        return null;
    }

    private _updateRepresentation(filters: Filter[]) {
        let count = 0;
        let representations = {};
        var currentSet = filters.filter(x => !Array.isArray(x.filteR1) || x.filteR1.length == 0);
        if (currentSet.length == 0) {
            console.warn("error! no leaves detected");
            return;
        }
        var iteration = 0;
        while (count < filters.length) {
            for (let index in currentSet) {
                var representation = [{ isBeginParenthesis: true } as Term];
                var isFirst = true;
                var o;
                currentSet[index].type == "AND" ? o = "und" : o = "oder";
                for (let condition of currentSet[index].filtercondition) {
                    if (!isFirst) representation.push({ isOperator: true, serializedStatement: o, node: currentSet[index].mathJSNode } as any);
                    var c = condition.attribute;
                    switch (condition.operation) {
                        case FilterConditionOperation.EQUAL:
                            c += " ist gleich ";
                            break;
                        case FilterConditionOperation.UNEQUAL:
                            c += " ist ungleich ";
                            break;
                        case FilterConditionOperation.INCLUDES:
                            c += " enthält ";
                            break;
                        case FilterConditionOperation.INCLUDESNOT:
                            c += " enthält nicht ";
                            break;
                    }
                    c += (condition as any).value;
                    representation.push({ isConstant: true, serializedStatement: c } as any);
                    isFirst = false;
                }
                if (Array.isArray(currentSet[index].filteR1)) {
                    for (let child of currentSet[index].filteR1) {
                        if (!isFirst) representation.push({ isOperator: true, serializedStatement: o } as any);
                        let i = filters.indexOf(child);
                        let childRep = representations[i.toString()];
                        if (childRep) representation.push(...childRep);
                        isFirst = false;
                    }
                }
                representation.push({ isEndParenthesis: true } as Term);
                representations[filters.indexOf(currentSet[index]).toString()] = representation;
                count++;
            }
            if (count < filters.length) {
                currentSet = filters.filter((x, index) => typeof (representations[index.toString()]) == 'undefined' && Array.isArray(x.filteR1) && !x.filteR1.some(function (y) {
                    let i = filters.indexOf(y);
                    return typeof (representations[i.toString()]) == 'undefined';
                }));
            }
            iteration++;
            if (iteration == 20) throw ("forced stop! infinit loop detected!");
        }
        this.compiledStatement = representation;
        this._statementValid.next(true);
    }

    private _getRootFilter(filters: Filter[]): Filter {
        if (!Array.isArray(filters)) return null;
        let candidates = [...filters];
        for (let x of filters) {
            if (!Array.isArray(x.filteR1)) continue;
            if (filters.some(y => y.filteR1.indexOf(x) > -1)) {
                let candidateIndex = candidates.indexOf(x);
                if (candidateIndex > -1) candidates.splice(candidateIndex, 1);
            }
        }
        if (candidates.length !== 1) console.warn(`WARNING: found ${candidates.length} root filters ...`); 7
        return candidates[0];
    }
}